import { z } from 'zod';
import { IsoTime } from './basics.js';
import { SessionInfo } from './auth.js';
import { Email, Password, Timezone, UserId, UserRole } from './user.js';
/**
 * Accounts after the first (knowledge doc sections 15 and 17; P8.4).
 *
 * The first admin comes from the installer's setup link. Everyone after comes from
 * an **invitation** an admin makes in the dashboard, and a forgotten password is
 * answered with a **reset link** an admin makes. Both are one-time links in the
 * setup link's shape: 32 random bytes after `#`, so the token reaches no request
 * line and no log, shown once to the admin who made it, stored only as a hash.
 * The admin passes it on however they talk to the person; Bartizo sends no email
 * (section 20 puts notifications in phase 9).
 *
 * Nobody but the person who will type a password ever chooses it.
 */
export const ACCOUNT_LINK_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;
export const AccountLinkToken = z.string().regex(ACCOUNT_LINK_TOKEN_PATTERN);
/** Long enough to reach somebody over a weekend; short enough not to linger. */
export const INVITE_TTL_HOURS = 72;
/** A reset is asked for by someone waiting for it. */
export const RESET_TTL_HOURS = 24;
export const AccountLinkPurpose = z.enum(['invite', 'reset']);
export const AccountLinkId = z.uuid();
/** An open link as the users page lists it. Never the token. */
export const AccountLinkSummary = z.object({
    id: AccountLinkId,
    purpose: AccountLinkPurpose,
    /** The address the account will have (an invitation) or has (a reset). */
    email: z.email(),
    /** The role an invitation grants; `null` for a reset. */
    role: UserRole.nullable(),
    /** The account a reset is for; `null` for an invitation. */
    userId: UserId.nullable(),
    createdByEmail: z.email().nullable(),
    createdAt: IsoTime,
    expiresAt: IsoTime,
});
/** `POST /users`: invite somebody, at a role. */
export const CreateInviteRequest = z.object({ email: Email, role: UserRole }).strict();
/**
 * The one response that carries the token. The dashboard puts it after `#` in a
 * link on its own origin; the API does not know the address people reach it by.
 */
export const CreatedAccountLink = z.object({
    link: AccountLinkSummary,
    token: AccountLinkToken,
});
/**
 * `PATCH /users/:id`: a role, or whether the account may sign in. Either changes
 * what the person can do, so either signs them out everywhere (S3).
 */
export const UpdateUserRequest = z
    .object({
    role: UserRole.optional(),
    disabled: z.boolean().optional(),
})
    .strict()
    .refine((patch) => patch.role !== undefined || patch.disabled !== undefined, {
    message: 'Nothing to change',
});
/** `POST /auth/account-link/inspect`: what a link is for, before a password is chosen. */
export const InspectAccountLinkRequest = z.object({ token: AccountLinkToken }).strict();
export const AccountLinkInfo = z.object({
    purpose: AccountLinkPurpose,
    email: z.email(),
    expiresAt: IsoTime,
});
/** `POST /auth/account-link`: spend it with the password the person chose. */
export const SpendAccountLinkRequest = z
    .object({
    token: AccountLinkToken,
    password: Password,
    timezone: Timezone.optional(),
})
    .strict();
/**
 * An invitation signs the new person in: the account is new and has no second
 * factor to skip. A reset does not: it signs every session out and leaves the
 * person to sign in with the new password **and** their authenticator code, so a
 * reset link alone never opens a session past a second factor.
 */
export const SpentAccountLink = z.object({
    purpose: AccountLinkPurpose,
    email: z.email(),
    session: SessionInfo.nullable(),
});
//# sourceMappingURL=account.js.map